/* eslint-disable complexity */
/* eslint-disable max-statements */
import { NUMBER } from "../../../constants/app-constants";
import { B2cFinanceService } from "../../../service";
import { getNextStep, FLOW_CONFIG } from "../../shared/pre-approval-on-fly-form/config";
import { setExperiment } from "../../shared/ab-experiment/actions";
import { runCloseSideEffect } from "../../shared/pre-approval-on-fly-form/close-side-effects";
import { getInhousePreApprovalLoanDetails } from "../pre-approval-financing-flow/pre-approval-loan-wrapper/actions";
import Types from "./types";

// Modal actions
const openModal = (entryPoint) => ({
    type: Types.OPEN_MODAL,
    entryPoint
});

// Step navigation actions
const setCurrentStep = (step) => ({
    type: Types.SET_CURRENT_STEP,
    step
});

// Form data actions
const setFormData = (answers) => ({
    type: Types.SET_FORM_DATA,
    answers
});

const updateFieldValue = (fieldId, value) => ({
    type: Types.UPDATE_FIELD_VALUE,
    fieldId,
    value
});

// Loading and error states
const setLoading = (isLoading) => ({
    type: Types.SET_LOADING,
    isLoading
});

const setError = (error) => ({
    type: Types.SET_ERROR,
    error
});

const resetState = () => ({
    type: Types.RESET_STATE
});

const getQueryParams = (state) => {
    const { isEditFlow, improveQuotesFlow, isCarFirstFlow } = state.preApprovalOnFly;
    const { orderId } = state.checkout?.order || {};
    return {
        ...(improveQuotesFlow && { improveQuotesFlow: true }),
        ...(isEditFlow && { editFlow: true }),
        ...(isCarFirstFlow && { isCarFirstFlow: true }),
        ...(orderId && { orderId })
    };
};

// Fetch config actions (GET /api/v2/preapproval/quote)
const fetchConfigRequest = () => ({
    type: Types.FETCH_CONFIG_REQUEST
});

const fetchConfigSuccess = (data) => ({
    type: Types.FETCH_CONFIG_SUCCESS,
    data
});

const fetchConfigFailure = (error) => ({
    type: Types.FETCH_CONFIG_FAILURE,
    error
});

let pendingFetchConfigPromise = null;

const fetchConfig = () => async (dispatch, getState) => {
    dispatch(fetchConfigRequest());
    const state = getState();
    const { user: { secureToken } } = state;
    const queryParams = getQueryParams(state);

    try {
        pendingFetchConfigPromise = B2cFinanceService.getPreApprovalOnFlyQuote(secureToken, queryParams);
        const response = await pendingFetchConfigPromise;
        dispatch(fetchConfigSuccess(response.data));
        if (response.data?.variant) {
            dispatch(setExperiment("preApprovalOnFlyDesktop", response.data.variant));
        }
        return response.data;
    } catch (error) {
        dispatch(fetchConfigFailure(error?.message || "Failed to fetch pre-approval data"));
        throw error;
    } finally {
        pendingFetchConfigPromise = null;
    }
};

// Submit step actions (PUT /quote or POST /submit)
const submitStepRequest = () => ({
    type: Types.SUBMIT_STEP_REQUEST
});

const submitStepSuccess = (data) => ({
    type: Types.SUBMIT_STEP_SUCCESS,
    data
});

const submitStepFailure = (error) => ({
    type: Types.SUBMIT_STEP_FAILURE,
    error
});

// PUT /api/v2/preapproval/quote - save step answers (actionKey === "UPDATE")
const updateStepAnswers = () => async (dispatch, getState) => {
    dispatch(submitStepRequest());
    const state = getState();
    const { user: { secureToken }, preApprovalOnFly: { answers, currentStep, initialStep } } = state;
    const queryParams = getQueryParams(state);

    try {
        const payload = {
            answers,
            currentStep,
            initialStep,
            variant: "VARIANT_B"
        };
        const response = await B2cFinanceService.updatePreApprovalOnFlyQuote(secureToken, payload, queryParams);
        dispatch(submitStepSuccess(response.data));
        return response.data;
    } catch (error) {
        dispatch(submitStepFailure(error?.message || "Failed to save answers"));
        throw error;
    }
};

// POST /api/v2/preapproval/submit - final submission (actionKey === "SUBMIT")
const submitPreApproval = () => async (dispatch, getState) => {
    dispatch(submitStepRequest());
    const state = getState();
    const { user: { secureToken }, preApprovalOnFly: { answers, currentStep, initialStep } } = state;
    const queryParams = getQueryParams(state);

    try {
        const payload = {
            answers,
            currentStep,
            initialStep,
            variant: "VARIANT_B"
        };
        const response = await B2cFinanceService.submitPreApprovalOnFly(secureToken, payload, queryParams);
        const { currentStep: _ignore, ...restData } = response.data;
        dispatch(submitStepSuccess(restData));
        return response.data;
    } catch (error) {
        dispatch(submitStepFailure(error?.message || "Failed to submit pre-approval"));
        throw error;
    }
};

// Poll actions (GET /api/v2/preapproval/poll)
const pollStart = () => ({
    type: Types.POLL_START
});

const pollSuccess = (data) => ({
    type: Types.POLL_SUCCESS,
    data
});

const pollFailure = (error) => ({
    type: Types.POLL_FAILURE,
    error
});

let pollTimerRef = null;

const startPolling = () => (dispatch, getState) => {
    dispatch(pollStart());

    const poll = async () => {
        try {
            const state = getState();
            const { user: { secureToken }, preApprovalOnFly: { isPolling } } = state;
            if (!isPolling) return;

            const queryParams = getQueryParams(state);
            const response = await B2cFinanceService.pollPreApprovalOnFly(secureToken, queryParams);
            const { pollStatus } = response.data || {};

            if (pollStatus === "COMPLETED" || pollStatus === "FAILED") {
                dispatch(pollSuccess(response.data));
                pollTimerRef = null;
            } else {
                // Continue polling
                pollTimerRef = window.setTimeout(poll, NUMBER.THREE_THOUSAND);
            }
        } catch (error) {
            dispatch(pollFailure(error?.message || "Polling failed"));
            pollTimerRef = null;
        }
    };

    poll();
};

const stopPolling = () => (dispatch) => {
    if (pollTimerRef) {
        window.clearTimeout(pollTimerRef);
        pollTimerRef = null;
    }
    dispatch(pollFailure("Polling stopped"));
};

// Combined submit handler - decides PUT vs POST based on actionKey
const submitStep = (actionKey) => async (dispatch, getState) => {
    const { user: { secureToken }, preApprovalOnFly: { isEditFlow } } = getState();

    // CASE 1: User is logged in — normal flow
    if (secureToken) {
        if (actionKey === "SUBMIT") {
            const data = await dispatch(submitPreApproval());
            dispatch(startPolling());
            return data;
        }
        if (isEditFlow) {
            return Promise.resolve({ skipApiCall: true });
        }
        return dispatch(updateStepAnswers());
    }

    // CASE 2: Not logged in — local only, skip API (login required at SUBMIT via component)
    return Promise.resolve({ skipApiCall: true });
};

// Set form config from local FORM_CONFIGS (fallback when API is not available)
const setFormConfigFromLocal = () => ({
    type: Types.SET_FORM_CONFIG_FROM_LOCAL
});

// Fetch form metadata actions (GET /config/metadata?scenario=PreApproval_V2_Form)
const fetchFormMetadataRequest = () => ({
    type: Types.FETCH_FORM_METADATA_REQUEST
});

const fetchFormMetadataSuccess = (data) => ({
    type: Types.FETCH_FORM_METADATA_SUCCESS,
    data
});

const fetchFormMetadataFailure = (error) => ({
    type: Types.FETCH_FORM_METADATA_FAILURE,
    error
});

const fetchFormMetadata = () => async (dispatch, getState) => {
    dispatch(fetchFormMetadataRequest());
    const { user: { secureToken } } = getState();

    try {
        const response = await B2cFinanceService.getPreApprovalQuestions({
            token: secureToken,
            scenario: "PreApproval_V2_Form"
        });
        dispatch(fetchFormMetadataSuccess(response.data));
        return response.data;
    } catch (error) {
        dispatch(fetchFormMetadataFailure(error?.message || "Failed to fetch form metadata"));
        throw error;
    }
};

// Set modal config (CTA text, redirect URLs)
const setModalConfig = (config) => ({
    type: Types.SET_MODAL_CONFIG,
    config
});

// Set edit flow flag
const setEditFlow = (isEditFlow) => ({
    type: Types.SET_EDIT_FLOW,
    isEditFlow
});

// Set initial answers (for pre-population in edit flow)
const setInitialFormData = (answers) => ({
    type: Types.SET_INITIAL_FORM_DATA,
    answers
});

const clearResultPageKey = () => async (dispatch) => {
    dispatch({ type: Types.SET_IMPROVE_QUOTES_FLOW, improveQuotesFlow: true });
    try {
        await dispatch(fetchConfig());
    } catch {
        // GET failed — proceed anyway with existing state
    }
    dispatch({ type: Types.CLEAR_RESULT_PAGE_KEY });
};

const setImproveQuotesFlow = (improveQuotesFlow) => ({
    type: Types.SET_IMPROVE_QUOTES_FLOW,
    improveQuotesFlow
});

const clearResultPageKeyOnly = () => ({
    type: Types.CLEAR_RESULT_PAGE_KEY
});

const setCarFirstFlow = (isCarFirstFlow) => ({
    type: Types.SET_CAR_FIRST_FLOW,
    isCarFirstFlow
});

const closeModal = ({ withSideEffects = false } = {}) => (dispatch, getState) => {
    const state = getState();
    const { refetchOnClose, entryPoint } = state.preApprovalOnFly;
    dispatch({ type: Types.CLOSE_MODAL });
    if (withSideEffects) {
        runCloseSideEffect(state);
    }
    if (refetchOnClose) {
        dispatch(fetchConfig());
    }
    if (entryPoint?.startsWith("cdp-") || entryPoint === "td-confirmation-banner") {
        dispatch(getInhousePreApprovalLoanDetails({ fetchListingDetails: true }));
    }
};

// Start pre-approval on-fly flow - can be called from anywhere
const startPreApprovalOnFly = (config = {}) => async (dispatch, getState) => {
    const {
        entryPoint = "unknown",
        ctaText,
        ctaSecondaryText,
        redirectUrl,
        secondaryRedirectUrl,
        nextSteps,
        showLoginOnSuccess = false,
        isEditFlow = false,
        initialFormData = null,
        skipFetch = false,
        isCarFirstFlow = false,
        refetchOnClose = false,
        reloadOnClose = false
    } = config;

    // 1. Open modal (delay for trigger path until async work + step navigation completes)
    if (skipFetch && !isEditFlow) {
        // Trigger path: modal will be opened after PUT call and step navigation
    } else {
        dispatch(openModal(entryPoint));
    }
    dispatch(setCarFirstFlow(isCarFirstFlow));

    if (refetchOnClose) {
        dispatch({ type: Types.SET_REFETCH_ON_CLOSE, refetchOnClose: true });
    }
    if (reloadOnClose) {
        dispatch({ type: Types.SET_RELOAD_ON_CLOSE, reloadOnClose: true });
    }

    // Check if user was previously rejected — reset to edit flow
    const { preApprovalOnFly: { resultPageKey } } = getState();
    if (resultPageKey === "REJECTED") {
        dispatch(setEditFlow(true));
    }

    // 2. Set edit flow flag if applicable
    if (isEditFlow) {
        dispatch(setEditFlow(true));
    }

    // 3. Set modal config if custom values provided
    if (ctaText || ctaSecondaryText || redirectUrl || secondaryRedirectUrl || nextSteps || showLoginOnSuccess) {
        dispatch(setModalConfig({
            ctaText,
            ctaSecondaryText,
            redirectUrl,
            secondaryRedirectUrl,
            nextSteps,
            showLoginOnSuccess
        }));
    }

    // 4. Fetch form metadata config and initial state from API
    if (!skipFetch) {
        const { user: { secureToken: token } } = getState();
        if (token) {
            try {
                await Promise.all([
                    dispatch(fetchFormMetadata()).catch(() => {}),
                    dispatch(fetchConfig())
                ]);
            } catch (error) {
                // Fallback to local config if API fails
                dispatch(setFormConfigFromLocal());
            }
        } else {
            dispatch(setFormConfigFromLocal());
            await dispatch(fetchFormMetadata()).catch(() => {});
        }
    }

    // 4b. For edit flow, clear result screen so the form is shown
    if (getState().preApprovalOnFly.isEditFlow) {
        dispatch({ type: Types.CLEAR_RESULT_PAGE_KEY });
    }

    // 5. Handle initial form data from other entry points (non-trigger callers)
    if (initialFormData && Object.keys(initialFormData).length > 0) {
        dispatch(setInitialFormData(initialFormData));
    }

    // 5b. Prefill user email if logged in and not already set
    const { user: { email: loggedInEmail }, preApprovalOnFly: { answers: currentAnswers } } = getState();
    if (loggedInEmail && !currentAnswers?.userEmail) {
        dispatch(setInitialFormData({ userEmail: loggedInEmail }));
    }

    // 6. Auto-advance past PAGE_1 (inline step) only when caller already collected PAGE_1 data
    //    (e.g., trigger component passes skipFetch: true after rendering PAGE_1 inline)
    if (!isEditFlow && skipFetch) {
        // Wait for any in-flight fetchConfig (from trigger's useEffect) to complete
        // BEFORE reading state. This prevents a late-arriving fetchConfigSuccess
        // from overwriting step/answers after the user has progressed in the modal.
        if (pendingFetchConfigPromise) {
            try {
                await pendingFetchConfigPromise;
            } catch {
                // fetchConfig failed, continue with local navigation
            }
        }

        const { preApprovalOnFly: { answers, currentStep }, user: { secureToken: tokenNow } } = getState();

        if (!currentStep || currentStep === "PAGE_1_FORM") {
            if (tokenNow) {
                try {
                    await dispatch(updateStepAnswers());
                } catch (error) {
                    // API save failed, continue with local navigation
                }
            }

            const stepForNavigation = currentStep || FLOW_CONFIG.defaultStep;
            const nextStep = getNextStep(stepForNavigation, answers);
            if (nextStep) {
                dispatch(setCurrentStep(nextStep));
            }
        }

        // Open modal after async work and step navigation are done
        dispatch(openModal(entryPoint));
    }
};

export {
    openModal,
    closeModal,
    setCurrentStep,
    setFormData,
    updateFieldValue,
    setLoading,
    setError,
    resetState,
    fetchConfig,
    fetchFormMetadata,
    submitStep,
    updateStepAnswers,
    submitPreApproval,
    pollStart,
    pollSuccess,
    pollFailure,
    startPolling,
    stopPolling,
    setFormConfigFromLocal,
    setModalConfig,
    setEditFlow,
    setInitialFormData,
    startPreApprovalOnFly,
    clearResultPageKey,
    clearResultPageKeyOnly,
    setCarFirstFlow,
    setImproveQuotesFlow
};
